/**
 * FAQSection - Two-column split layout
 * Left: title + subtitle + contact CTA | Right: improved accordion
 */
import React, { useState } from 'react';
import { Typography, Box, Container } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

import { COLORS, TRANSITIONS, RADIUS, FAQ_ITEMS, TYPOGRAPHY, SPACING } from './landingTheme';
import { ScrollReveal } from './animations';

// Single FAQ item — improved with number + better spacing
const FAQItem = ({ item, index, isOpen, onToggle }) => (
  <Box
    onClick={onToggle}
    sx={{
      cursor: 'pointer',
      py: { xs: 2, md: 2.5 },
      borderBottom: `1px solid ${COLORS.gray200}`,
      '&:last-child': { borderBottom: 'none' },
      '&:hover .faq-toggle': {
        background: isOpen ? COLORS.accent : COLORS.gray200,
      },
    }}
  >
    {/* Question row */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: { xs: 1.5, md: 2 },
      }}
    >
      {/* Number */}
      <Typography
        sx={{
          fontSize: '0.8rem',
          fontWeight: 700,
          color: isOpen ? COLORS.accent : COLORS.gray400,
          minWidth: 24,
          pt: 0.15,
          transition: TRANSITIONS.normal,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {String(index + 1).padStart(2, '0')}
      </Typography>

      {/* Question text */}
      <Typography
        sx={{
          flex: 1,
          fontSize: { xs: '0.95rem', md: '1.05rem' },
          fontWeight: 600,
          color: isOpen ? COLORS.primary : COLORS.textPrimary,
          transition: TRANSITIONS.fast,
          lineHeight: 1.5,
        }}
      >
        {item.question}
      </Typography>

      {/* Toggle */}
      <Box
        className="faq-toggle"
        sx={{
          width: 32,
          height: 32,
          borderRadius: RADIUS.full,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          background: isOpen ? COLORS.accent : COLORS.gray100,
          color: isOpen ? '#0f2440' : COLORS.textMuted,
          transition: TRANSITIONS.normal,
          transform: isOpen ? 'rotate(0deg)' : 'rotate(0deg)',
          mt: 0.15,
        }}
      >
        {isOpen ? <RemoveIcon sx={{ fontSize: 18 }} /> : <AddIcon sx={{ fontSize: 18 }} />}
      </Box>
    </Box>

    {/* Answer — smooth expand */}
    <Box
      sx={{
        maxHeight: isOpen ? 400 : 0,
        overflow: 'hidden',
        transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
        opacity: isOpen ? 1 : 0,
      }}
    >
      <Typography
        sx={{
          color: COLORS.textSecondary,
          fontSize: { xs: '0.85rem', md: '0.92rem' },
          lineHeight: 1.75,
          pl: { xs: 4.5, md: 5 },
          pt: 1,
          pb: 1,
        }}
      >
        {item.answer}
      </Typography>
    </Box>
  </Box>
);

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <Box sx={{ py: SPACING.sectionY, background: COLORS.white, position: 'relative', overflow: 'hidden' }}>
      {/* Decorative blob */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '-5%',
          left: '-8%',
          width: 350,
          height: 350,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(245, 158, 11, 0.04) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg">
        {/* Two-column layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' },
            gap: { xs: 4, md: 8 },
            alignItems: 'start',
          }}
        >
          {/* Left column — sticky on desktop */}
          <ScrollReveal>
            <Box
              sx={{
                position: { md: 'sticky' },
                top: { md: 100 },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              <Typography
                sx={{
                  color: COLORS.accent,
                  fontSize: TYPOGRAPHY.tiny,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  mb: 1.5,
                }}
              >
                FAQ
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  fontSize: TYPOGRAPHY.h2,
                  fontWeight: 800,
                  color: COLORS.textPrimary,
                  letterSpacing: '-0.02em',
                  mb: 2,
                  lineHeight: 1.2,
                }}
              >
                Preguntas frecuentes
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  color: COLORS.textSecondary,
                  lineHeight: 1.7,
                  mb: 4,
                  maxWidth: { xs: 400, md: 'none' },
                  mx: { xs: 'auto', md: 0 },
                }}
              >
                Todo lo que necesitas saber sobre NARA y cómo puede transformar la atención de tu consultorio.
              </Typography>

              {/* Contact CTA */}
              <Box
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  background: COLORS.gray50,
                  border: `1px solid ${COLORS.gray200}`,
                  display: { xs: 'none', md: 'block' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${COLORS.accent}15, ${COLORS.accent}08)`,
                      border: `1px solid ${COLORS.accent}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <HelpOutlineIcon sx={{ fontSize: 20, color: COLORS.accent }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      color: COLORS.textPrimary,
                    }}
                  >
                    ¿Tienes otra pregunta?
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    color: COLORS.textSecondary,
                    lineHeight: 1.6,
                    mb: 1.5,
                  }}
                >
                  Nuestro equipo está listo para ayudarte con cualquier duda sobre la plataforma.
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.8,
                    color: COLORS.primary,
                    cursor: 'pointer',
                    transition: TRANSITIONS.fast,
                    '&:hover': { color: COLORS.accent },
                  }}
                >
                  <EmailOutlinedIcon sx={{ fontSize: 16 }} />
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                    soporte@nara.health
                  </Typography>
                </Box>
              </Box>
            </Box>
          </ScrollReveal>

          {/* Right column — accordion */}
          <ScrollReveal delay={0.15} direction="right">
            <Box
              sx={{
                background: COLORS.white,
                borderRadius: 4,
                border: `1px solid ${COLORS.gray200}`,
                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                px: { xs: 2.5, md: 4 },
                py: { xs: 0.5, md: 1 },
              }}
            >
              {FAQ_ITEMS.map((item, idx) => (
                <FAQItem
                  key={idx}
                  item={item}
                  index={idx}
                  isOpen={openIndex === idx}
                  onToggle={() => setOpenIndex(openIndex === idx ? -1 : idx)}
                />
              ))}
            </Box>
          </ScrollReveal>
        </Box>
      </Container>
    </Box>
  );
}
